import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/common";
import {
  Calculator,
  CircleHelp,
  GitBranch,
  Plus,
  UserPlus,
  UserMinus,
  Zap,
} from "lucide-react";
import { observer } from "mobx-react-lite";
import {
  useAssignmentStore,
  useDisclosureStore,
  useEntryDataStore,
  useEntryUIStore,
} from "../hooks/useDataEntryStore";
import { softDeleteDataEntry } from "../operations/entry.ops";
import { removeAssignment } from "../operations/assignment.ops";
import {
  canEditDisclosure,
  getAssigneeDisplayName,
} from "../domains/assignment";
import { useAuthStore } from "@/features/auth/hooks";
import { isNxmapMappableDisclosure } from "@/features/esg/constants/nxmap-mappable-disclosures.constants";
import { useTran } from "@/hooks";
import { humanizeFormula } from "@/features/esg/utils/formula-display";
import AssignDisclosureDialog from "./dialogs/AssignDisclosureDialog";
import NxmapImportDialog from "./dialogs/NxmapImportDialog";
import CreateEntriesDialog from "./dialogs/CreateEntriesDialog";
import CreateSubEntriesDialog from "./dialogs/CreateSubEntriesDialog";
import EditEntryDialog from "./dialogs/EditEntryDialog";
import EditSubEntriesDialog from "./dialogs/EditSubEntriesDialog";
import ProofDialog from "./dialogs/ProofDialog";
import { Can } from "@/hooks/useCan/Can";

const anyNxmapImportedInList = (list) =>
  Array.isArray(list) && list.some((e) => e?.nxmapImported);

const hasAnyNxmapImported = (...sources) => {
  for (const src of sources) {
    const byOrg = src?.dataEntries;
    if (!byOrg) continue;
    for (const yearMap of Object.values(byOrg)) {
      for (const yearData of Object.values(yearMap || {})) {
        const entries = yearData?.entries;
        if (anyNxmapImportedInList(entries)) return true;
        if (entries && typeof entries === "object") {
          for (const group of Object.values(entries)) {
            if (anyNxmapImportedInList(group?.entries)) return true;
          }
        }
      }
    }
  }
  return false;
};

const DisclosureHeader = observer(() => {
  const { t } = useTran();
  const { activeDisclosure, frameworks } = useDisclosureStore();
  const framework = activeDisclosure?.framework || "";
  const frameworkName =
    frameworks.find((fw) => fw.code === framework)?.name || framework;
  const { isSuperAdmin, user, isJJSEA } = useAuthStore();
  const permissions = user?.permissions || [];
  const { getAssignment, setShowAssignDialog } = useAssignmentStore();
  const { standardEntries, subEntries, computedEntries } = useEntryDataStore();
  const {
    deletingEntryId,
    deletingEntry: isDeleting,
    setShowCreateEntriesDialog,
    setDeletingEntryId,
  } = useEntryUIStore();

  const assignment = getAssignment(activeDisclosure?._id);
  const assigneeName = getAssigneeDisplayName(assignment);
  const editable = canEditDisclosure(assignment);

  const handleConfirmDelete = async () => {
    const success = await softDeleteDataEntry(deletingEntryId);
    if (success) setDeletingEntryId(null);
  };

  const {
    code,
    parentCode,
    parentTitle,
    topic,
    defaultUnit,
    inputType,
    isCalculated,
    formula,
    formulaScope,
    title,
    crossMapping,
    baselineYear,
    carbonEmissionMultiplier,
  } = activeDisclosure || {};

  // Custom disclosures have no parentCode/parentTitle (they group by topic
  // instead) — show the Grouping Topic beside the code in that case.
  const groupLabel = parentCode ? parentTitle : topic;

  const isSubEntry = inputType === "subEntries";
  const isComputed = isCalculated && inputType !== "subEntries";
  const hasNxmap = Boolean(user?.nxmapConnected);
  const hasNxmapImportedEntries = hasAnyNxmapImported(
    standardEntries,
    subEntries,
    computedEntries,
  );
  const isNxmapImport =
    (hasNxmap && !isComputed && isNxmapMappableDisclosure(framework, code)) ||
    hasNxmapImportedEntries;

  return (
    <div className="space-y-1">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge className="bg-primary/10 text-primary text-xs font-semibold">
            {code}
          </Badge>
          {groupLabel && (
            <Badge className="text-xs bg-muted text-muted-foreground">
              {t(groupLabel)}
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          {isSuperAdmin &&
            (assignment ? (
              <Can
                resource="esg_data_entry"
                action="create"
                userPermissions={permissions}
              >
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => removeAssignment(activeDisclosure._id)}
                >
                  <UserMinus size={16} />
                  {t("Unassign")}
                </Button>
              </Can>
            ) : (
              !isNxmapImport &&
              !isJJSEA && (
                <Can
                  resource="esg_data_entry"
                  action="create"
                  userPermissions={permissions}
                >
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowAssignDialog(true)}
                  >
                    <UserPlus size={16} />
                    {t("Delegate Other")}
                  </Button>
                </Can>
              )
            ))}
          {!isComputed && editable && !isNxmapImport && (
            <Can
              resource="esg_data_entry"
              action="create"
              userPermissions={permissions}
            >
              <Button
                size="sm"
                onClick={() => setShowCreateEntriesDialog(true)}
              >
                <Plus size={16} />
                Add Entry
              </Button>
            </Can>
          )}
        </div>
      </div>

      <h2 className="text-lg font-semibold">{t(title)}</h2>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span className="text-sky-600">{frameworkName}</span>
        <span>{defaultUnit ? `Unit: ${defaultUnit}` : ""}</span>
        {assigneeName && (
          <Badge variant="secondary" className="text-xs">
            {t("Assigned to")} {assigneeName}
          </Badge>
        )}
      </div>

      {crossMapping?.mappings?.length > 0 && (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <GitBranch size={14} />
          <span>
            {t("Also mapped in")}:{" "}
            {crossMapping.mappings
              .map((m) => `${m.framework} ${m.disclosureCode}`)
              .join(", ")}
          </span>
        </div>
      )}

      {(formula || carbonEmissionMultiplier != null) && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
          {formula && (
            <div className="flex items-center gap-1.5">
              <Calculator size={14} />
              <span>
                {isComputed
                  ? `${t("Calculated")}: ${humanizeFormula(formula, { baselineYear })}`
                  : formulaScope === "subEntries"
                    ? `${t("Sub-entry formula")}: ${humanizeFormula(formula, { baselineYear })}`
                    : `${t("Formula")}: ${humanizeFormula(formula, { baselineYear })}`}
              </span>
            </div>
          )}

          {carbonEmissionMultiplier != null && (
            <div className="flex items-center gap-1.5">
              <Zap size={14} />
              <span>
                {t("Carbon Emission Multiplier")}: {carbonEmissionMultiplier}
              </span>
            </div>
          )}
        </div>
      )}

      {isSubEntry ? <CreateSubEntriesDialog /> : <CreateEntriesDialog />}
      <EditEntryDialog />
      {isSubEntry && <EditSubEntriesDialog />}
      <ProofDialog />
      <NxmapImportDialog />
      <AssignDisclosureDialog />
      <ConfirmDialog
        open={!!deletingEntryId}
        onOpenChange={(v) => !v && setDeletingEntryId(null)}
        title={t("Delete Entry")}
        description={t(
          "Are you sure you want to delete this entry? This action cannot be undone.",
        )}
        confirmLabel={t("Delete")}
        cancelLabel={t("Cancel")}
        onConfirm={handleConfirmDelete}
        loading={isDeleting}
        tone="danger"
      />
    </div>
  );
});

export default DisclosureHeader;
